<template>
    <div>
        <div class="backdrop" v-if="showOptions" @click="showOptions = false"></div>

        <div class="select-title">
            {{ title }}
        </div>
        <div :class="['select-container', { vertical: verticalChips }]">
            <div class="select-option-container" ref="selectContainer">
                <div class="select-option-value" @click="toggleShowOptions" ref="selectInput">
                    <input class="search-input" @input="handleInputSearch" v-model="search" type="text" />
                    <i class="fas fa-chevron-down"></i>
                </div>
                <div class="custom-select-options" v-if="showOptions" ref="dropdownMenu" :style="dropdownPosition" @scroll="handleScroll">
                    <div class="select-option" @click="updateOption()">
                        {{ defaultSelectPlaceholder }}
                    </div>
                    <div v-if="extraText" class="select-option no-highlight" @click="updateOption()">
                        {{ extraText }}
                    </div>
                    <div
                        class="select-option"
                        @click="updateOption($event, option)"
                        v-for="option of filteredOptionsSearch"
                        :key="option.id"
                    >
                        <div :class="[{ 'my-2': option.children ? option.children.length > 0 : false }]">
                            {{ option.title }}
                            <span v-if="option.sku" class="sku-text">({{ option.sku }})</span>
                        </div>
                        <div v-if="option.children">
                            <div
                                class="select-option select-option-child"
                                @click.stop="updateOptionChild($event, optionChild)"
                                v-for="optionChild of option.children"
                                :key="optionChild.id"
                            >
                                {{ optionChild.title }}
                                <span v-if="optionChild.sku" class="sku-text">({{ optionChild.sku }})</span>
                            </div>
                        </div>
                    </div>
                    <div v-if="filteredOptions.length == 0 && !extraText" class="select-option">No Items Available</div>
                    
                    <!-- Loading indicator -->
                    <div v-if="isLoading" class="select-option loading-indicator">
                        <div class="loading-spinner"></div>
                        <span>Loading more items...</span>
                    </div>
                    
                    <!-- End of results message -->
                    <div v-if="!isLoading && reachedEnd && filteredOptionsSearch.length > 0" class="select-option end-message">
                        No more items available
                    </div>
                </div>
            </div>
            <div :class="['select-chip-container', { 'vertical-chips': verticalChips }, { 'vertical-chips-row': verticalChipsRow }]">
                <div class="select-chip" v-for="selectedValue of selectedValuesMapped" :key="selectedValue.id">
                    <div>
                        {{ selectedValue.title }}
                        <span v-if="selectedValue.sku" class="sku-badge" title="SKU">{{ selectedValue.sku }}</span>
                    </div>
                    <div @click="removeItem(selectedValue)">
                        <i class="fas fa-times select-chip-remove"></i>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>
<script>
import { getPageScrollElement } from "../../../utils/scroll-container"

export default {
    data() {
        return {
            showOptions: false,
            search: "",
            showAllMode: false,
            dropdownPosition: {
                position: 'fixed',
                top: '0px',
                left: '0px',
                width: '100%',
                minWidth: '200px'
            },
            // Pagination properties
            currentPage: 1,
            totalPages: 0,
            isLoading: false,
            reachedEnd: false,
            pageScrollElement: null
        }
    },
    props: {
        extraText: {
            type: String,
            default: "",
        },
        defaultSelectPlaceholder: {
            type: String,
            default: "",
        },
        verticalChips: {
            type: Boolean,
            default: false,
        },
        verticalChipsRow: {
            type: Boolean,
            default: false,
        },
        title: {
            type: String,
            required: true,
        },
        options: {
            // array with objects of id, title, sku
            type: Array,
            default: () => [],
        },
        selectedValues: {
            type: Array,
            default: () => [],
        },
        // New props for pagination
        supportsPagination: {
            type: Boolean,
            default: false,
        },
        totalItemCount: {
            type: Number,
            default: 0,
        },
        storeObjectMode: {
            type: Boolean,
            default: false,
        },
        externalSearch: {
            type: Boolean,
            default: false,
        }
    },
    mounted() {
        this.search = this.defaultSelectPlaceholder
        this.pageScrollElement = getPageScrollElement()
        window.addEventListener('resize', this.positionDropdown)
        const scrollTarget = this.pageScrollElement || window
        scrollTarget.addEventListener('scroll', this.positionDropdown)
        
        // Reset pagination when component mounts
        this.resetPagination()
    },
    methods: {
        resetPagination() {
            this.currentPage = 1
            this.totalPages = 0
            this.isLoading = false
            this.reachedEnd = false
        },
        handleInputSearch() {
            this.showAllMode = false
            this.showOptions = true
            this.resetPagination() // Reset pagination on new search
            this.$nextTick(() => {
                this.positionDropdown()
            })
            
            // Emit search event with page 1
            this.$emit("search", this.search, 1)
        },
        toggleShowOptions() {
            this.showOptions = !this.showOptions
            if (this.showOptions) {
                this.showAllMode = true
                this.$nextTick(() => {
                    this.positionDropdown()
                })
            }
        },
        positionDropdown() {
            if (!this.showOptions || !this.$refs.selectInput || !this.$refs.dropdownMenu) return
            
            const inputRect = this.$refs.selectInput.getBoundingClientRect()
            const dropdownHeight = this.$refs.dropdownMenu.offsetHeight
            const viewportHeight = window.innerHeight
            
            // Check if dropdown would go below viewport
            const spaceBelow = viewportHeight - inputRect.bottom
            const spaceAbove = inputRect.top
            const dropdownFitsBelow = spaceBelow >= dropdownHeight
            
            // Position the dropdown
            if (dropdownFitsBelow || spaceBelow >= spaceAbove) {
                // Position below
                this.dropdownPosition = {
                    position: 'fixed',
                    top: `${inputRect.bottom + 5}px`,
                    left: `${inputRect.left}px`,
                    width: `${inputRect.width}px`,
                    minWidth: '200px'
                }
            } else {
                // Position above
                this.dropdownPosition = {
                    position: 'fixed',
                    bottom: `${window.innerHeight - inputRect.top + 5}px`,
                    left: `${inputRect.left}px`,
                    width: `${inputRect.width}px`,
                    minWidth: '200px'
                }
            }
        },
        handleScroll(event) {
            if (!this.supportsPagination || this.isLoading || this.reachedEnd) return;
            
            const element = event.target;
            const scrollPosition = element.scrollTop + element.clientHeight;
            const scrollThreshold = element.scrollHeight * 0.8; // 80% of total height
            
            if (scrollPosition >= scrollThreshold) {
                // Load more results
                this.loadMoreResults();
            }
        },
        loadMoreResults() {
            if (this.isLoading || this.reachedEnd) return;
            
            this.isLoading = true;
            const nextPage = this.currentPage + 1;
            
            // Emit event to parent component to load more data
            this.$emit("loadMore", this.search, nextPage);
        },
        updateOption(e, option) {
            let updateValue

            if (!option) {
                updateValue = []
                this.search = this.defaultSelectPlaceholder
            } else {
                this.search = ""
                if (option.children && option.children.length) {
                    if (this.storeObjectMode) {
                        updateValue = [...this.selectedValues];
                        for (let child of option.children) {
                            updateValue.push({
                                id: child.id,
                                title: child.title,
                                sku: child.sku
                            });
                        }
                    } else {
                        updateValue = this.selectedValues.slice()
                        for (let child of option.children) {
                            updateValue = updateValue.concat(child.id)
                        }
                    }
                } else {
                    if (this.storeObjectMode) {
                        updateValue = [...this.selectedValues, {
                            id: option.id,
                            title: option.title,
                            sku: option.sku
                        }];
                    } else {
                        updateValue = this.selectedValues.concat(option.id)
                    }
                }
            }
            
            this.$emit("update", updateValue)
            this.showOptions = false
        },
        updateOptionChild(e, option) {
            let updateValue;
            
            if (!option) {
                updateValue = this.storeObjectMode ? [] : [];
            } else {
                if (this.storeObjectMode) {
                    updateValue = [...this.selectedValues, {
                        id: option.id,
                        title: option.title,
                        sku: option.sku
                    }];
                } else {
                    updateValue = this.selectedValues.concat(option.id);
                }
            }
            
            this.$emit("update", updateValue)
            this.showOptions = false
        },
        removeItem(option) {
            let updateValue;
            
            if (this.storeObjectMode) {
                updateValue = this.selectedValues.filter(item => item.id !== option.id);
            } else {
                updateValue = this.selectedValues.filter(item => item !== option.id);
            }
            
            this.$emit("update", updateValue)
        },
        // Method to be called from parent to update pagination status
        updatePaginationStatus(page, totalPages, reachedEnd = false) {
            this.currentPage = page;
            this.totalPages = totalPages;
            this.isLoading = false;
            this.reachedEnd = reachedEnd;
        }
    },
    computed: {
        selectedValuesMapped() {
            if (this.storeObjectMode) {
                // If we're already storing full objects, just return them
                return this.selectedValues;
            }
            
            // Otherwise, map IDs to objects from the options array
            return this.selectedValues
                .map((id) => {
                    for (let option of this.options) {
                        let found = option.id === id ? option : false

                        if (found) {
                            return found
                        }
                        if (option.children) {
                            for (let optionChild of option.children) {
                                found = optionChild.id === id ? optionChild : false
                                if (found) {
                                    return found
                                }
                            }
                        }
                    }
                    return null
                })
                .filter((item) => !!item)
        },
        filteredOptions() {
            return this.options.filter((option) => {
                if (this.storeObjectMode) {
                    return !this.selectedValues.some(item => item.id === option.id);
                } else {
                    return !this.selectedValues.includes(option.id);
                }
            })
        },
        filteredOptionsSearch() {
            const normalizedSearch = this.search.trim().toLowerCase()
            const searchTokens = normalizedSearch.split(/\s+/).filter(Boolean)
            const options =
                !normalizedSearch || this.showAllMode
                    ? this.options
                    : this.externalSearch
                    ? this.options.filter((option) => {
                          const searchableText = `${option.title || ""} ${option.sku || ""}`.toLowerCase()
                          return searchTokens.every((token) => searchableText.includes(token))
                      })
                    : this.options
                          .filter((option) => {
                              // Search in title and SKU
                              return option.title.toLowerCase().includes(normalizedSearch) ||
                                    (option.sku && option.sku.toLowerCase().includes(normalizedSearch));
                          })
                          .map((item) => {
                              return {
                                  ...item,
                                  children: item.children
                                      ? item.children.filter((child) => {
                                            return child.title.toLowerCase().includes(normalizedSearch) ||
                                                  (child.sku && child.sku.toLowerCase().includes(normalizedSearch));
                                       })
                                      : [],
                              }
                          })

            return options
                .filter((item) => {
                    if (this.storeObjectMode) {
                        return !this.selectedValues.some(selected => selected.id === item.id);
                    } else {
                        return !this.selectedValues.includes(item.id);
                    }
                })
                .map((item) => {
                    return {
                        ...item,
                        children: item.children ? item.children.filter((child) => {
                            if (this.storeObjectMode) {
                                return !this.selectedValues.some(selected => selected.id === child.id);
                            } else {
                                return !this.selectedValues.includes(child.id);
                            }
                        }) : [],
                        childrenAllSelected:
                            item.children && item.children.length > 0
                                ? item.children.length == item.children.filter((child) => {
                                    if (this.storeObjectMode) {
                                        return this.selectedValues.some(selected => selected.id === child.id);
                                    } else {
                                        return this.selectedValues.includes(child.id);
                                    }
                                }).length
                                : false,
                    }
                })
                .filter((item) => !item.childrenAllSelected)
        },
    },
    watch: {
        search(e) {
            this.$emit("search", e)
        },
    },
    beforeDestroy() {
        window.removeEventListener('resize', this.positionDropdown)
        const scrollTarget = this.pageScrollElement || window
        scrollTarget.removeEventListener('scroll', this.positionDropdown)
    },
}
</script>
<style lang="scss" scoped>
@import "../../../assets/variables.scss";

.select-title {
    color: $textColor;
    margin-bottom: 8px;
    font-weight: 500;
}

.select-option,
.select-option-value {
    padding: 8px 15px;
    cursor: pointer;
}

.select-option-value {
    display: flex;
    border-radius: 6px;
    justify-content: space-between;
    align-items: center;
    padding: 8px 15px;
    background-color: white;
    border: 1px solid $lightBorderColor;
    transition: all 0.2s ease;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
    
    &:hover {
        border-color: $secondaryColor;
    }
}

.select-option {
    transition: all 0.2s ease;
    background-color: white;
    border: none;
    border-bottom: 1px solid $lightBorderColor;
    
    &:hover:not(.no-highlight) {
        background-color: rgba($primaryColor, 0.05);
    }
    
    &:last-child {
        border-bottom: none;
    }
}

.select-option-child {
    padding-left: 30px;
    
    &:hover:not(.no-highlight) {
        background-color: rgba($primaryColor, 0.08);
    }
}

.select-chip-container {
    display: flex;
    flex: 1;
    flex-direction: row;
    gap: 10px;
    margin-top: 10px;
}

.select-chip {
    background-color: rgba($secondaryColor, 0.15);
    color: darken($secondaryColor, 20%);
    padding: 6px 12px;
    border-radius: 6px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    font-size: 0.9em;
    border: 1px solid rgba($secondaryColor, 0.2);
    transition: all 0.2s ease;
    
    &:hover {
        background-color: rgba($secondaryColor, 0.2);
    }
}

.select-chip-remove {
    cursor: pointer;
    font-size: 13px;
    color: $accentColor;
    
    &:hover {
        color: darken($accentColor, 10%);
    }
}

.vertical-chips {
    flex-direction: column;
    margin-top: 15px;
    font-size: 0.8em;
    
    .select-chip {
        padding: 8px 12px;
    }
}

.select-option-container {
    position: relative;
    flex: 1;
}

.vertical-chips-row {
    flex-wrap: wrap;
    flex-direction: row;
}

.custom-select-options {
    max-height: 280px;
    border-radius: 6px;
    overflow-y: auto;
    z-index: 9999;
    border: 1px solid $lightBorderColor;
    background-color: white;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    
    &::-webkit-scrollbar {
        width: 8px;
    }
    
    &::-webkit-scrollbar-track {
        background: #f1f1f1;
        border-radius: 6px;
    }
    
    &::-webkit-scrollbar-thumb {
        background: lighten($primaryColor, 20%);
        border-radius: 6px;
        
        &:hover {
            background: lighten($primaryColor, 10%);
        }
    }
}

.select-container {
    display: flex;
    gap: 10px;
    &.vertical {
        flex-direction: column;
    }
}

.backdrop {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: transparent;
    z-index: 5;
}

.search-input {
    outline: none;
    border: none;
    width: 100%;
    padding: 7px;
    background-color: transparent;
    color: $textColor;
    
    &::placeholder {
        color: lighten($textColor, 30%);
    }
}

/* Loading spinner styles */
.loading-indicator {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 12px;
    color: $textColor;
    background-color: rgba($lightBorderColor, 0.1);
    
    .loading-spinner {
        width: 16px;
        height: 16px;
        border: 2px solid rgba($primaryColor, 0.3);
        border-top: 2px solid $primaryColor;
        border-radius: 50%;
        margin-right: 8px;
        animation: spin 1s linear infinite;
    }
}

@keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
}

.end-message {
    text-align: center;
    font-size: 0.9em;
    color: lighten($textColor, 20%);
    padding: 12px;
    background-color: rgba($lightBorderColor, 0.1);
    border-top: 1px solid $lightBorderColor;
}

.sku-text {
    font-size: 0.9em;
    color: #666;
    margin-left: 4px;
}

.sku-badge {
    background-color: #f0f0f0;
    padding: 2px 4px;
    border-radius: 3px;
    font-size: 0.75em;
    margin-left: 4px;
    color: #666;
}
</style>
